import { Injectable, Provider, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
    ATOM_FILE_ITEM_CONFIG,
    ATOM_FILE_UPLOAD_CONFIG,
    AtomFileItemConfig,
    AtomFileUploadConfig,
} from "@atomchat-io/ui-design-system";
import { TranslocoService } from "@jsverse/transloco";
import { catchError, map, of, switchMap } from "rxjs";

const BASE_KEY_PATH = "atom_file_upload";

/**
 * Bridges the `atom-file-upload` and `atom-file-item` copy
 * (`ATOM_FILE_UPLOAD_CONFIG`, `ATOM_FILE_ITEM_CONFIG`) to Transloco.
 *
 * Most of that copy takes a context (file name, sizes, counts), so every entry
 * is a function rather than a `Signal`. Each one touches the loaded-language
 * signal before translating, which re-runs the component's `computed()` on
 * live language switches, same approach as {@link AtomFilterRowLabelsService}.
 */
@Injectable({
    providedIn: "root",
})
export class AtomFileUploadLabelsService {
    private readonly translocoService = inject(TranslocoService);
    /**
     * Ticks once the active language's catalog is loaded, not on the switch
     * itself: `translate()` returns the raw key until then. A failed load
     * ticks anyway, so the copy never throws.
     */
    private readonly loadedLang = toSignal(
        this.translocoService.langChanges$.pipe(
            switchMap((lang) =>
                this.translocoService.load(lang).pipe(
                    map(() => lang),
                    catchError(() => of(lang)),
                ),
            ),
        ),
    );

    readonly uploadConfig: AtomFileUploadConfig = {
        promptSingle: () => this.translate("prompt_single"),
        promptMultiple: () => this.translate("prompt_multiple"),
        constraints: ({ accept, maxSize }) =>
            [
                accept.map((type) => type.toUpperCase()).join(", "),
                maxSize
                    ? this.translate("constraints_max", { size: maxSize })
                    : "",
            ]
                .filter(Boolean)
                .join(" · "),
        countRejected: ({ max, added, attempted }) =>
            this.translate(
                max === 1 ? "count_rejected_one" : "count_rejected_plural",
                { max, added, attempted },
            ),
        duplicateRejected: ({ name, names }) =>
            names.length > 1
                ? this.translate("duplicate_rejected_plural", {
                      name,
                      count: names.length - 1,
                  })
                : this.translate("duplicate_rejected_one", { name }),
        announceAdded: ({ count }) =>
            this.translate(
                count === 1 ? "announce_added_one" : "announce_added_plural",
                { count },
            ),
        announceRejected: ({ name, reason }) =>
            this.translate("announce_rejected", {
                name,
                reason: this.translate(`rejection_reasons.${reason}`),
            }),
        announceRemoved: ({ name }) =>
            this.translate("announce_removed", { name }),
        announceCompleted: ({ name }) =>
            this.translate("announce_completed", { name }),
        announceLimitReached: ({ max }) =>
            this.translate("announce_limit_reached", { max }),
    };

    readonly itemConfig: AtomFileItemConfig = {
        uploadedLabel: () => this.translate("item.uploaded_label"),
        uploadingMeta: ({ loaded, total, percent }) =>
            this.translate("item.uploading_meta", { loaded, total, percent }),
        uploadedMeta: ({ size, date }) =>
            date ? this.translate("item.uploaded_meta", { size, date }) : size,
        rejectedMeta: ({ size, reason, maxSize }) => {
            if (reason === "size" && maxSize) {
                return this.translate("item.rejected_size", { size, maxSize });
            }
            return reason === "type"
                ? this.translate("item.rejected_type", { size })
                : size;
        },
        cancelLabel: ({ name }) =>
            this.translate("item.cancel_label", { name }),
        retryLabel: ({ name }) => this.translate("item.retry_label", { name }),
        deleteLabel: ({ name }) =>
            this.translate("item.delete_label", { name }),
        removeLabel: ({ name }) =>
            this.translate("item.remove_label", { name }),
        downloadLabel: ({ name }) =>
            this.translate("item.download_label", { name }),
    };

    private translate(key: string, params?: Record<string, unknown>): string {
        this.loadedLang();
        return this.translocoService.translate(
            `${BASE_KEY_PATH}.${key}`,
            params,
        );
    }
}

export const provideAtomFileUploadLabels = (): Provider[] => [
    {
        provide: ATOM_FILE_UPLOAD_CONFIG,
        useFactory: (): AtomFileUploadConfig =>
            inject(AtomFileUploadLabelsService).uploadConfig,
    },
    {
        provide: ATOM_FILE_ITEM_CONFIG,
        useFactory: (): AtomFileItemConfig =>
            inject(AtomFileUploadLabelsService).itemConfig,
    },
];
